import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Paper,
  Fade,
  Breadcrumbs,
  Link,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  IconButton,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  LiveTv as LiveTvIcon,
  AccountBalance as PaymentIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as ReportIcon,
  MonetizationOn as MonetizationOnIcon,
  Description as DescriptionIcon,
  PhoneIphone as PhoneIphoneIcon,
  CardGiftcard as CardGiftcardIcon,
  Alarm as AlarmIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import {
  DashboardSummary,
  UserManagement,
  ScheduleManagement,
  LivestreamTable,
  AdvancePaymentManagement,
  NotificationManagement,
  RevenueManagement,
  CommissionManagement,
  AgreementManagement,
  AppVersionManagement,
  PeriodBonusManagement,
  LateIncidentManagement,
  PenaltyManagement
} from './admin';
import {
  getAllLivestreams,
  updateLivestream,
  deleteLivestream,
  getUserSummaries,
  getMonthlySummary,
  getWeeklySummary,
  getCustomRangeSummary,
  getPeriodSummaries
} from '../services/livestreamService';
import { getUserData, removeUserData } from '../services/storageService';
import { getAllUsers, createUser, updateUser, deleteUser } from '../services/authService';
import { getAllSchedules } from '../services/scheduleService';

// Theme màu chuyên nghiệp
const adminTheme = {
  primary: '#1e3a8a', // Blue 900
  secondary: '#059669', // Green 600
  accent: '#7c3aed', // Purple 600
  warning: '#d97706', // Orange 600
  error: '#dc2626', // Red 600
  success: '#059669', // Green 600
  info: '#0284c7', // Sky 600
  background: {
    default: '#f8fafc', // Gray 50
    paper: '#ffffff',
    dark: '#0f172a' // Slate 900
  },
  text: {
    primary: '#1e293b', // Slate 800
    secondary: '#64748b' // Slate 500
  }
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`admin-tabpanel-${index}`}
    aria-labelledby={`admin-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Fade in={true} timeout={300}>
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      </Fade>
    )}
  </div>
);

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const user = getUserData();

  // Core state
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [livestreams, setLivestreams] = useState([]);
  const [userSummaries, setUserSummaries] = useState([]);
  const [filteredSummaries, setFilteredSummaries] = useState([]);
  const [periodSummaries, setPeriodSummaries] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notificationCount, setNotificationCount] = useState(3);
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLivestream, setSelectedLivestream] = useState(null);
  const [editForm, setEditForm] = useState({ date: new Date(), hours: '' });
  
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Summary state
  const [selectedUser, setSelectedUser] = useState('');
  const [summaryType, setSummaryType] = useState('month');
  const [summaryDate, setSummaryDate] = useState(new Date());
  const [customDateRange, setCustomDateRange] = useState({
    startDate: new Date(new Date().setDate(1)),
    endDate: new Date()
  });
  const [summaryData, setSummaryData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [selectedUserForSummary, setSelectedUserForSummary] = useState('');

  // Schedule management state
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: new Date(),
    endDate: new Date()
  });

  // Tab configuration
  const tabs = [
    { 
      label: 'Dashboard', 
      icon: <DashboardIcon />, 
      color: adminTheme.primary,
      description: 'Tổng quan hệ thống' 
    },
    { 
      label: 'Người dùng', 
      icon: <PeopleIcon />, 
      color: adminTheme.secondary,
      description: 'Quản lý tài khoản' 
    },
    { 
      label: 'Livestream', 
      icon: <LiveTvIcon />, 
      color: adminTheme.accent,
      description: 'Lịch sử chấm công' 
    },
    { 
      label: 'Lịch trình', 
      icon: <ScheduleIcon />, 
      color: adminTheme.warning,
      description: 'Quản lý ca làm' 
    },
    { 
      label: 'Tạm ứng', 
      icon: <PaymentIcon />, 
      color: adminTheme.info,
      description: 'Yêu cầu tạm ứng' 
    },
    { 
      label: 'Thông báo', 
      icon: <NotificationsIcon />, 
      color: adminTheme.error,
      description: 'Quản lý thông báo' 
    },
    { 
      label: 'Doanh thu', 
      icon: <MonetizationOnIcon />, 
      color: adminTheme.success,
      description: 'Doanh thu livestream' 
    },
    {
      label: 'Hoa hồng',
      icon: <MonetizationOnIcon />,
      color: adminTheme.warning,
      description: 'Hoa hồng livestream'
    },
    {
      label: 'Thưởng cuối kỳ',
      icon: <CardGiftcardIcon />,
      color: adminTheme.warning,
      description: 'Thưởng thêm cho user'
    },
    {
      label: 'Điều khoản',
      icon: <DescriptionIcon />,
      color: adminTheme.info,
      description: 'Điều khoản & hợp đồng'
    },
    {
      label: 'Phiên bản',
      icon: <PhoneIphoneIcon />,
      color: adminTheme.accent,
      description: 'Quản lý version app'
    },
    {
      label: 'Chế tài trễ',
      icon: <AlarmIcon />,
      color: adminTheme.error,
      description: 'Ghi nhận đi live trễ'
    },
    {
      label: 'Xử phạt',
      icon: <GavelIcon />,
      color: adminTheme.error,
      description: 'Phạt vi phạm doanh thu / GMV ảo'
    }
  ];

  // Enhanced notification function
  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  }, []);

  // Data fetching functions
  const initFetch = async () => {
    setLoading(true);
    try {
      const [usersResult, livestreamsResult, summariesResult] = await Promise.all([
        getAllUsers(),
        getAllLivestreams(),
        getUserSummaries()
      ]);

      if (usersResult.success) {
        setUsers(usersResult.data);
      } else {
        showNotification('Không thể lấy danh sách người dùng', 'error');
      }

      if (livestreamsResult.success) {
        setLivestreams(livestreamsResult.data.livestreams || []);
      } else {
        showNotification('Không thể lấy dữ liệu livestream', 'error');
      }

      if (summariesResult.success) {
        setUserSummaries(summariesResult.data || []);
        setFilteredSummaries(summariesResult.data || []);
      } else {
        showNotification('Không thể lấy tổng hợp dữ liệu', 'error');
      }
    } catch (error) {
      console.error('Lỗi khởi tạo dữ liệu:', error);
      showNotification('Có lỗi xảy ra khi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (!selectedUser) return;

    try {
      let result;
      const currentDate = new Date(summaryDate);
      const year = currentDate.getFullYear();

      if (summaryType === 'month') {
        const month = currentDate.getMonth() + 1;
        result = await getMonthlySummary(selectedUser, year, month);
      } else if (summaryType === 'week') {
        const week = Math.ceil((currentDate.getDate() + new Date(year, currentDate.getMonth(), 1).getDay()) / 7);
        result = await getWeeklySummary(selectedUser, year, week);
      } else if (summaryType === 'custom') {
        result = await getCustomRangeSummary(
          selectedUser,
          customDateRange.startDate.toISOString(),
          customDateRange.endDate.toISOString()
        );
      }

      if (result.success) {
        setSummaryData(result.data);
      } else {
        showNotification(result.error?.message || 'Không thể lấy dữ liệu tổng hợp', 'error');
      }
    } catch (error) {
      console.error('Lỗi lấy dữ liệu tổng hợp:', error);
      showNotification('Có lỗi khi lấy dữ liệu tổng hợp', 'error');
    }
  };

  const fetchPeriodSummaries = async () => {
    try {
      const result = await getPeriodSummaries(selectedPeriod, selectedUserForSummary);
      if (result.success) {
        setPeriodSummaries(Array.isArray(result.data) ? result.data : []);
      } else {
        showNotification(result.error?.message || 'Không thể lấy dữ liệu kỳ', 'error');
        setPeriodSummaries([]);
      }
    } catch (error) {
      console.error('Lỗi lấy dữ liệu kỳ:', error);
      showNotification('Có lỗi khi lấy dữ liệu kỳ', 'error');
      setPeriodSummaries([]);
    }
  };

  const fetchSchedules = async () => {
    try {
      const result = await getAllSchedules();
      if (result.success) {
        setSchedules(result.data || []);
        setFilteredSchedules(result.data || []);
      } else {
        showNotification(result.error?.message || 'Không thể lấy dữ liệu lịch', 'error');
        setSchedules([]);
        setFilteredSchedules([]);
      }
    } catch (error) {
      console.error('Lỗi lấy dữ liệu lịch:', error);
      showNotification('Có lỗi khi lấy dữ liệu lịch', 'error');
      setSchedules([]);
      setFilteredSchedules([]);
    }
  };

  // Event handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    removeUserData();
    navigate('/login');
  };

  const handleEdit = (livestream) => {
    setSelectedLivestream(livestream);
    setEditForm({
      date: new Date(livestream.date),
      hours: livestream.hours.toString()
    });
    setOpenDialog(true);
  };

  const handleDelete = async (livestreamId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa bản ghi này?')) {
      try {
        const result = await deleteLivestream(livestreamId);
        if (result.success) {
          setLivestreams(livestreams.filter(item => item._id !== livestreamId));
          showNotification('Xóa bản ghi thành công');
        } else {
          showNotification(result.error?.message || 'Không thể xóa bản ghi', 'error');
        }
      } catch (error) {
        console.error('Lỗi xóa bản ghi:', error);
        showNotification('Có lỗi khi xóa bản ghi', 'error');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLivestream(null);
    setEditForm({ date: new Date(), hours: '' });
  };

  const handleSaveEdit = async () => {
    try {
      const result = await updateLivestream(selectedLivestream._id, {
        date: editForm.date,
        hours: parseFloat(editForm.hours)
      });

      if (result.success) {
        setLivestreams(livestreams.map(item => 
          item._id === selectedLivestream._id 
            ? { ...item, date: editForm.date, hours: parseFloat(editForm.hours) }
            : item
        ));
        showNotification('Cập nhật bản ghi thành công');
        handleCloseDialog();
      } else {
        showNotification(result.error?.message || 'Không thể cập nhật bản ghi', 'error');
      }
    } catch (error) {
      console.error('Lỗi cập nhật bản ghi:', error);
      showNotification('Có lỗi khi cập nhật bản ghi', 'error');
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Effects
  useEffect(() => {
    // Chỉ fetch data khi component mount
    initFetch();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchSummary();
    }
  }, [selectedUser, summaryType, summaryDate, customDateRange]);

  useEffect(() => {
    fetchPeriodSummaries();
  }, [selectedPeriod, selectedUserForSummary]);

  useEffect(() => {
    // Fetch schedules khi users đã load xong
    if (users.length > 0) {
      fetchSchedules();
    }
  }, [users]);

  // Render loading state
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${adminTheme.primary} 0%, ${adminTheme.secondary} 100%)`
        }}
      >
        <Card sx={{ p: 4, minWidth: 300, textAlign: 'center' }}>
          <CardContent>
            <AdminIcon sx={{ fontSize: 48, color: adminTheme.primary, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Đang tải trang quản trị...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Vui lòng chờ trong giây lát
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Main render
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        minHeight: '100vh', 
        background: adminTheme.background.default,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Enhanced Header */}
        <AppBar 
          position="sticky" 
          elevation={0}
          sx={{ 
            background: `linear-gradient(135deg, ${adminTheme.primary} 0%, ${adminTheme.secondary} 100%)`,
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Container maxWidth="xl">
            <Toolbar sx={{ px: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                <AdminIcon sx={{ mr: 2, fontSize: 28 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
                    Admin Portal
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Carnaval Management System By dinhdai.tech
                  </Typography>
                </Box>
              </Box>

              {/* Actions */}
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton color="inherit">
                  <Badge badgeContent={notificationCount} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
                
                <Button
                  onClick={handleUserMenuClick}
                  startIcon={
                    <Avatar 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        bgcolor: adminTheme.accent,
                        fontSize: 14
                      }}
                    >
                      {user?.fullName?.charAt(0) || 'A'}
                    </Avatar>
                  }
                  color="inherit"
                  sx={{ 
                    ml: 1,
                    textTransform: 'none',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ textAlign: 'left', ml: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {user?.fullName || 'Admin'}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Quản trị viên
                    </Typography>
                  </Box>
                </Button>
              </Stack>
            </Toolbar>
          </Container>
        </AppBar>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          sx={{ mt: 1 }}
        >
          <MenuItem onClick={handleUserMenuClose}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cài đặt</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Đăng xuất</ListItemText>
          </MenuItem>
        </Menu>

        {/* Breadcrumb */}
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              color="inherit"
              href="#"
              onClick={() => navigate('/')}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Trang chủ
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <AdminIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              {tabs[activeTab]?.label}
            </Typography>
          </Breadcrumbs>
        </Container>

        {/* Enhanced Navigation Tabs */}
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid #e2e8f0'
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 72,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                },
                '& .Mui-selected': {
                  color: adminTheme.primary,
                  background: 'linear-gradient(135deg, rgba(30,58,138,0.1) 0%, rgba(5,150,105,0.1) 100%)'
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  background: `linear-gradient(135deg, ${adminTheme.primary} 0%, ${adminTheme.secondary} 100%)`
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={
                    <Box sx={{ 
                      color: activeTab === index ? tab.color : 'inherit',
                      mb: 0.5
                    }}>
                      {tab.icon}
                    </Box>
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="inherit">
                        {tab.label}
                      </Typography>
                      {!isMobile && (
                        <Typography variant="caption" color="text.secondary">
                          {tab.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Paper>
        </Container>

        {/* Content Area */}
        <Container maxWidth="xl" sx={{ flexGrow: 1, pb: 4 }}>
          {/* Tab Panels */}
          <TabPanel value={activeTab} index={0}>
            <DashboardSummary 
              users={users}
              livestreams={livestreams}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              summaryType={summaryType}
              setSummaryType={setSummaryType}
              summaryDate={summaryDate}
              setSummaryDate={setSummaryDate}
              customDateRange={customDateRange}
              setCustomDateRange={setCustomDateRange}
              summaryData={summaryData}
              userSummaries={userSummaries}
              filteredSummaries={filteredSummaries}
              setFilteredSummaries={setFilteredSummaries}
              selectedPeriod={selectedPeriod}
              setSelectedPeriod={setSelectedPeriod}
              selectedUserForSummary={selectedUserForSummary}
              setSelectedUserForSummary={setSelectedUserForSummary}
              periodSummaries={periodSummaries}
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <UserManagement 
              users={users}
              setUsers={setUsers}
              userSummaries={userSummaries}
              showNotification={showNotification}
              createUser={createUser}
              updateUser={updateUser}
              deleteUser={deleteUser}
              fetchData={initFetch}
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <LivestreamTable 
              livestreams={livestreams}
              users={users}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              isMobile={isMobile}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <ScheduleManagement 
              users={users}
              schedules={schedules}
              filteredSchedules={filteredSchedules}
              setFilteredSchedules={setFilteredSchedules}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              dateRangeFilter={dateRangeFilter}
              setDateRangeFilter={setDateRangeFilter}
              fetchSchedules={fetchSchedules}
              showNotification={showNotification}
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <AdvancePaymentManagement 
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={5}>
            <NotificationManagement 
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={6}>
            <RevenueManagement 
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={7}>
            <CommissionManagement
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={8}>
            <PeriodBonusManagement
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={9}>
            <AgreementManagement
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={10}>
            <AppVersionManagement
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={11}>
            <LateIncidentManagement
              isMobile={isMobile}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={12}>
            <PenaltyManagement
              isMobile={isMobile}
            />
          </TabPanel>
        </Container>

        {/* Edit Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ 
            background: `linear-gradient(135deg, ${adminTheme.primary} 0%, ${adminTheme.secondary} 100%)`,
            color: 'white',
            mb: 2
          }}>
            Chỉnh sửa bản ghi livestream
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <DatePicker
                label="Ngày"
                value={editForm.date}
                onChange={(newValue) => setEditForm({...editForm, date: newValue})}
                renderInput={(params) => (
                  <TextField {...params} fullWidth sx={{ mb: 2 }} />
                )}
              />
              <TextField
                fullWidth
                label="Số giờ"
                type="number"
                value={editForm.hours}
                onChange={(e) => setEditForm({...editForm, hours: e.target.value})}
                inputProps={{ step: 0.5, min: 0 }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog} variant="outlined">
              Hủy
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              variant="contained"
              sx={{
                background: `linear-gradient(135deg, ${adminTheme.primary} 0%, ${adminTheme.secondary} 100%)`
              }}
            >
              Lưu
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Notification */}
        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.severity}
            variant="filled"
            sx={{ 
              minWidth: 300,
              '& .MuiAlert-icon': {
                fontSize: 20
              }
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminDashboard; 
